import React, { useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Plus, Pencil, X, Save } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { ukDate } from "../../lib/dates";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const upcomingSundays = (n = 8) => {
  const d = new Date(); d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
  return Array.from({ length: n }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i * 7); return iso(x); });
};
const sel = "bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none px-3 py-2 text-sm min-w-0";

const WeekEditor = ({ team, week, initial, onDone }) => {
  const [slots, setSlots] = useState(initial?.length ? initial.map((s) => ({ role: s.role, member_id: s.member_id || "" })) : [{ role: "", member_id: "" }]);
  const set = (i, k, v) => setSlots((s) => s.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    try {
      await api.put(`/portal/my-teams/${team.id}/rota/${week}`, { slots: slots.filter((s) => s.role.trim()) });
      toast.success(`Rota saved for ${ukDate(week)}`); onDone(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: "var(--sj-border)" }} data-testid={`rota-editor-${week}`}>
      {slots.map((s, i) => (
        <div key={i} className="flex flex-wrap sm:flex-nowrap gap-2">
          <input data-testid={`rota-role-${i}`} value={s.role} maxLength={60} onChange={(e) => set(i, "role", e.target.value)} placeholder="Role e.g. Sound desk" className={`${sel} flex-1`} />
          <select data-testid={`rota-member-${i}`} value={s.member_id} onChange={(e) => set(i, "member_id", e.target.value)} className={`${sel} flex-1`}>
            <option value="">Unfilled</option>
            {team.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button onClick={() => setSlots((x) => x.filter((_, j) => j !== i))} title="Remove slot" className="p-2 rounded-full hover:bg-red-50 shrink-0" style={{ color: "#C0392B" }} data-testid={`rota-remove-slot-${i}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2 pt-1">
        <button data-testid="rota-add-slot" onClick={() => setSlots((x) => [...x, { role: "", member_id: "" }])} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 border" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}><Plus className="h-3.5 w-3.5" /> Add slot</button>
        <div className="flex-1" />
        <button onClick={() => onDone(false)} className="text-xs font-semibold rounded-full px-3 py-1.5 border" style={{ borderColor: "var(--sj-border)" }} data-testid="rota-cancel">Cancel</button>
        <button data-testid="rota-save" onClick={save} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-4 py-1.5 text-white" style={{ background: "var(--sj-primary)" }}><Save className="h-3.5 w-3.5" /> Save week</button>
      </div>
    </div>
  );
};

const WeekView = ({ w, team, reload }) => {
  const pending = (role) => (team.my_release_requests || []).some((r) => r.week === w.week && r.role === role);
  const offered = (role) => (team.my_cover_offers || []).some((r) => r.week === w.week && r.role === role);
  const cover = async (role) => {
    try { await api.post(`/portal/my-teams/${team.id}/rota/${w.week}/cover`, { role }); toast.success("Thanks! Your leader will confirm"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const release = async (role) => {
    const note = window.prompt(`Ask to come off ${role} on Sunday ${ukDate(w.week)}? Add a short note for your leader (optional):`, "");
    if (note === null) return;
    try { await api.post(`/portal/my-teams/${team.id}/rota/${w.week}/release`, { role, note }); toast.success("Request sent to your team leader"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {w.slots.map((s, i) => {
        const mine = s.member_id && s.member_id === team.my_member_id;
        return (
          <span key={i} className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1" data-testid={`rota-slot-${w.week}-${i}`}
            style={{ background: mine ? "#FEF6D8" : s.member_name ? "#0B5E4E14" : "#C0392B14", color: mine ? "#8a6a00" : s.member_name ? "var(--sj-primary)" : "#C0392B" }}>
            <span><strong>{s.role}:</strong> {s.member_name || "Unfilled"}{mine && " (you)"}</span>
            {mine && (pending(s.role)
              ? <span className="font-semibold italic" data-testid={`rota-release-pending-${w.week}-${i}`}>· swap requested</span>
              : <button data-testid={`rota-release-${w.week}-${i}`} onClick={() => release(s.role)} className="font-semibold underline">Can't make it</button>)}
            {!s.member_id && team.my_member_id && team.members.some((m) => m.is_me) && (offered(s.role)
              ? <span className="font-semibold italic" data-testid={`rota-cover-pending-${w.week}-${i}`}>· you offered</span>
              : <button data-testid={`rota-cover-${w.week}-${i}`} onClick={() => cover(s.role)} className="font-semibold underline">I can cover</button>)}
          </span>
        );
      })}
    </div>
  );
};

const CoverOffers = ({ team, reload }) => {
  const list = team.cover_offers || [];
  if (!list.length) return null;
  const decide = (c, action) => api.post(`/portal/my-teams/${team.id}/rota-covers/${c.id}/${action}`)
    .then(() => { toast.success(action === "accept" ? `${c.member_name} is now on the rota` : "Offer declined"); reload(); })
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  return (
    <div className="rounded-xl p-3 mb-2 space-y-2" style={{ background: "#E3F1EC" }} data-testid={`rota-cover-offers-${team.id}`}>
      <div className="text-xs font-semibold" style={{ color: "var(--sj-primary)" }}>Cover offers ({list.length})</div>
      {list.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-2 bg-white rounded-lg p-2" data-testid={`rota-cover-offer-${c.id}`}>
          <span className="text-sm flex-1 min-w-[10rem]"><strong>{c.member_name}</strong> can cover {c.role} on {ukDate(c.week)}</span>
          <button data-testid={`rota-cover-accept-${c.id}`} onClick={() => decide(c, "accept")} className="text-xs font-semibold rounded-full px-3 py-1.5 text-white" style={{ background: "var(--sj-primary)" }}>Accept</button>
          <button data-testid={`rota-cover-decline-${c.id}`} onClick={() => decide(c, "decline")} className="text-xs font-semibold rounded-full px-3 py-1.5 border" style={{ borderColor: "var(--sj-border)" }}>Decline</button>
        </div>
      ))}
    </div>
  );
};

const ReleaseRequests = ({ team, reload }) => {
  const list = team.release_requests || [];
  if (!list.length) return null;
  const decide = (r, action) => api.post(`/portal/my-teams/${team.id}/rota-releases/${r.id}/${action}`)
    .then(() => { toast.success(action === "approve" ? `${r.member_name} taken off — slot is now unfilled` : "Request declined"); reload(); })
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  return (
    <div className="rounded-xl p-3 mb-2 space-y-2" style={{ background: "#FEF6D8" }} data-testid={`rota-release-requests-${team.id}`}>
      <div className="text-xs font-semibold" style={{ color: "#8a6a00" }}>Swap requests ({list.length})</div>
      {list.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-2 bg-white rounded-lg p-2" data-testid={`rota-release-${r.id}`}>
          <span className="text-sm flex-1 min-w-[10rem]"><strong>{r.member_name}</strong> can't do {r.role} on {ukDate(r.week)}{r.note && <em className="block text-xs" style={{ color: "var(--sj-text-3)" }}>"{r.note}"</em>}</span>
          <button data-testid={`rota-release-approve-${r.id}`} onClick={() => decide(r, "approve")} className="text-xs font-semibold rounded-full px-3 py-1.5 text-white" style={{ background: "var(--sj-primary)" }}>Approve</button>
          <button data-testid={`rota-release-decline-${r.id}`} onClick={() => decide(r, "decline")} className="text-xs font-semibold rounded-full px-3 py-1.5 border" style={{ borderColor: "var(--sj-border)" }}>Decline</button>
        </div>
      ))}
    </div>
  );
};

export const TeamRota = ({ team, reload }) => {
  const [editing, setEditing] = useState(null);
  const [newWeek, setNewWeek] = useState("");
  const byWeek = Object.fromEntries(team.rota.map((w) => [w.week, w]));
  const free = upcomingSundays().filter((d) => !byWeek[d]);
  if (!team.can_manage && !team.rota.length) return null;
  const done = (changed) => { setEditing(null); setNewWeek(""); if (changed) reload(); };
  return (
    <div className="mt-5" data-testid={`team-rota-${team.id}`}>
      <h3 className="font-semibold text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "var(--sj-text-3)" }}><CalendarRange className="h-4 w-4" /> Serving rota</h3>
      {team.can_manage && <CoverOffers team={team} reload={reload} />}
      {team.can_manage && <ReleaseRequests team={team} reload={reload} />}
      {!team.rota.length && <p className="text-sm mb-2" style={{ color: "var(--sj-text-3)" }}>No rota planned yet.</p>}
      <div className="space-y-2">
        {team.rota.map((w) => (
          <div key={w.week} className="rounded-xl p-3" style={{ background: "var(--sj-bg)" }} data-testid={`rota-week-${w.week}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-sm font-semibold flex-1">Sunday {ukDate(w.week)}</span>
              {team.can_manage && editing !== w.week && <button data-testid={`rota-edit-${w.week}`} onClick={() => setEditing(w.week)} className="p-1 rounded hover:bg-black/5" style={{ color: "var(--sj-primary)" }}><Pencil className="h-3.5 w-3.5" /></button>}
            </div>
            {editing === w.week ? <WeekEditor team={team} week={w.week} initial={w.slots} onDone={done} /> : <WeekView w={w} team={team} reload={reload} />}
          </div>
        ))}
      </div>
      {team.can_manage && !editing && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2 items-center">
            <select data-testid={`rota-new-week-${team.id}`} value={newWeek} onChange={(e) => setNewWeek(e.target.value)} className={sel}>
              <option value="">Plan a Sunday…</option>
              {free.map((d) => <option key={d} value={d}>{`Sunday ${ukDate(d)}`}</option>)}
            </select>
          </div>
          {newWeek && <WeekEditor key={newWeek} team={team} week={newWeek} onDone={done} />}
        </div>
      )}
    </div>
  );
};
